import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { getTrackById, importPromoImages } from '../services/trackService';
import type { Track } from '../services/trackTypes';
import { getTracks } from '../services/trackService';
import TrackHeader from './trackDetail/TrackHeader';
import AudioFilesSection from './trackDetail/AudioFilesSection';
import PromoPitchesSection from './trackDetail/PromoPitchesSection';
import PromoImagesSection from './trackDetail/PromoImagesSection';
import NotesSection from './trackDetail/NotesSection';
import VerticalVideosSection from './trackDetail/VerticalVideosSection';
import { binaryInactiveBodyKey, providerLabelForCloudError } from '../services/cloudErrorGuide';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { UsersIcon } from '@heroicons/react/24/outline';
import { ImageIcon, MusicIcon, FileTextIcon, VideoIcon, CameraIcon, MessageSquareIcon, FilmIcon, InfoIcon, XCircleIcon } from 'lucide-react';
import { logInfo, logError } from '../services/logtail';
import { getSessionData, clearSessionData } from '../utils/sessionRecovery';
import { updateTrackMetadata } from '../services/trackService';
// Tabs removed in favor of stacked accordions
import { Pencil } from 'lucide-react';
import { asyncUploadService } from '../services/asyncUploadService';
import CassetteLoader from '../components/CassetteLoader';
import { TRACK_STATUS_VALUES } from '../services/kanbanService';
import { SketchSection, SketchProgressBar } from '../components/sketchy';
import { useDesignMode } from '../contexts/DesignContext';
import { listShares, type TrackShare } from '../services/shareService';
import type { TrackPermissions } from '../types/track';
import { getTrackPermissions } from '../utils/trackPermissions';
import { classNames } from '../utils/classNames';
import StorageRequiredBanner from '../components/StorageRequiredBanner';
import { useStorageGate } from '../hooks/useStorageGate';

const TrackDetail: React.FC = () => {
  const { t } = useTranslation();
  const { isSketchy } = useDesignMode();
  const { id } = useParams<{ id:string }>();
  const [searchParams] = useSearchParams();
  
  // Define tabs first so we can use them in state initialization
  const tabs = [
    { key: 'track', name: t('track.track') },
    { key: 'verticalVideos', name: t('track.verticalVideos.label') },
  ];
  
  // Initialize selectedTab from URL or default to 0
  const initialTab = searchParams.get('tab');
  const tabIndex = tabs.findIndex(tab => tab.key === initialTab);
  const [selectedTab, setSelectedTab] = useState(tabIndex >= 0 ? tabIndex : 0);
  
  // Sync tab state with URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const newTabIndex = tabs.findIndex(tab => tab.key === tabFromUrl);
    if (newTabIndex >= 0 && newTabIndex !== selectedTab) {
      setSelectedTab(newTabIndex);
    }
  }, [searchParams, tabs, selectedTab]);
  const [Track, setTrack] = useState<Track | null>(null);
  const [loading, setLoading] = useState(true);
  const [artwork, setArtwork] = useState<string | null>(null);
  const [showArtworkUpload, setShowArtworkUpload] = useState(false);
  const [promoPitch, setPromoPitch] = useState<{ en: string; native: string }>({ en: '', native: '' });
  const [notes, setNotes] = useState('');
  const [secondLang, setSecondLang] = useState<string | null>(null);
  const [promoImages, setPromoImages] = useState<any[]>([]);
  const [audioFiles, setAudioFiles] = useState<Array<{ id: string; name: string; fileId: string; isPreview?: boolean }>>([]);
  const { userType, user, driveLinked } = useAuth();
  const { canMutate } = useStorageGate();
  const [permissions, setPermissions] = useState<TrackPermissions>({
    canRead: true,
    canUploadAudio: false,
    canUploadPromo: false,
    canAddAudioComments: false,
    canEditMetadata: false,
    canDelete: false,
    canShare: false,
  });
  const [shareCount, setShareCount] = useState(0);
  const [isOwner, setIsOwner] = useState(false);
  const [allAccordionsOpen, setAllAccordionsOpen] = useState(true);

  const gatedPermissions = useMemo<TrackPermissions>(() => {
    if (canMutate) return permissions;
    return {
      ...permissions,
      canUploadAudio: false,
      canUploadPromo: false,
      canAddAudioComments: false,
      canEditMetadata: false,
      canDelete: false,
      canShare: false,
    };
  }, [permissions, canMutate]);

  const showUploadGateBanner = !canMutate && (
    permissions.canUploadAudio ||
    permissions.canUploadPromo ||
    permissions.canEditMetadata ||
    permissions.canDelete ||
    permissions.canShare
  );

  // Add metadata state and handlers to TrackDetail
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({
    name: Track?.name || '',
    artist: Track?.artist || '',
    release: Track?.release || '',
    releaseDate: Track?.releaseDate || '',
    status: Track?.status || '',
    multilink: Track?.multilink || '',
    isSingle: Track?.isSingle || false,
    progress: Track?.progress || 0,
  });

  const artistInputRef = useRef<HTMLInputElement>(null);
  const releaseInputRef = useRef<HTMLInputElement>(null);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [availableTracks, setAvailableTracks] = useState<Track[]>([]);
  const [currentImportFileCount, setCurrentImportFileCount] = useState<number>(0);


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Complete':
        return 'var(--status-complete-bg) var(--status-complete-text) var(--status-complete-border)';
      case 'In Progress':
        return 'var(--status-progress-bg) var(--status-progress-text) var(--status-progress-border)';
      case 'Mixing and Mastering':
        return 'var(--status-mixing-bg) var(--status-mixing-text) var(--status-mixing-border)';
      case 'Draft':
        return 'var(--status-draft-bg) var(--status-draft-text) var(--status-draft-border)';
      case 'Demo':
        return 'var(--status-demo-bg) var(--status-demo-text) var(--status-demo-border)';
      case 'Published':
        return 'var(--status-published-bg) var(--status-published-text) var(--status-published-border)';
      case 'Live':
        return 'var(--status-live-bg) var(--status-live-text) var(--status-live-border)';
      default:
        return 'var(--status-default-bg) var(--status-default-text) var(--status-default-border)';
    }
  };

  const startEditMode = () => {
    if (!Track) return;
    setEditData({
      name: Track.name || '',
      artist: Track.artist || '',
      release: Track.release || '',
      releaseDate: Track.releaseDate || '',
      status: Track.status || '',
      multilink: Track.multilink || '',
      isSingle: Track.isSingle || false,
      progress: Track.progress || 0,
    });
    setEditMode(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      setEditData({ ...editData, [name]: e.target.checked });
    } else if (name === 'progress') {
      setEditData({ ...editData, [name]: Number(value) });
    } else {
      setEditData({ ...editData, [name]: value });
    }
  };

  const handleSave = async () => {
    if (!Track) return;
    setIsSaving(true);
    
    // Optimistically update the UI immediately
    const optimisticTrack = { ...Track, ...editData };
    setTrack(optimisticTrack);
    setEditMode(false);
    
    // Show success message immediately
    toast.success(t('track.metadataSaved'));
    
    // Reset loading state
    setIsSaving(false);
    
    // Update backend in background (non-blocking)
    updateTrackMetadata(Track.id, editData).catch((err) => {
      logError('Failed to update track metadata in background', { error: err });
      // Revert optimistic update on error
      setTrack(Track);
      toast.error(t('track.metadataSaveError'));
      // Re-enter edit mode so user can try again
      setEditMode(true);
    });
  };

  const handleCancel = () => {
    setEditData({
      name: Track?.name || '',
      artist: Track?.artist || '',
      release: Track?.release || '',
      releaseDate: Track?.releaseDate || '',
      status: Track?.status || '',
      multilink: Track?.multilink || '',
      isSingle: Track?.isSingle || false,
      progress: Track?.progress || 0,
    });
    setEditMode(false);
  };

  const toggleAllAccordions = () => {
    setAllAccordionsOpen(!allAccordionsOpen);
  };

  // Restore session data on mount
  useEffect(() => {
    const sessionData = getSessionData();
    if (sessionData && sessionData.page === `/tracks/${id}`) {
      logInfo('Restoring session data for track detail', { dataKeys: Object.keys(sessionData.data) });
      if (sessionData.data.promoPitch) setPromoPitch(sessionData.data.promoPitch);
      if (sessionData.data.promoImages) setPromoImages(sessionData.data.promoImages);
      if (sessionData.data.secondLang) setSecondLang(sessionData.data.secondLang);
      clearSessionData();
      toast.success(t('track.dataRestored'));
    }
  }, [id]);

  useEffect(() => {
    // Load autocomplete data and available tracks for Starter tier check
    if (user && driveLinked) {
      const cachedArtists = localStorage.getItem('cassette_artists');
      const cachedReleases = localStorage.getItem('cassette_releases');
      
      if (!cachedArtists || !cachedReleases) {
        logInfo('Loading autocomplete data for track detail');
        getTracks().then(tracks => {
          const artists = Array.from(new Set(tracks.map(t => t.artist).filter(Boolean)));
          const releases = Array.from(new Set(tracks.map(t => t.release).filter(Boolean)));
          localStorage.setItem('cassette_artists', JSON.stringify(artists));
          localStorage.setItem('cassette_releases', JSON.stringify(releases));
          logInfo('Autocomplete data loaded', { artistCount: artists.length, releaseCount: releases.length });
        }).catch(error => {
          logError('Failed to load autocomplete data', { 
            error: error instanceof Error ? error.message : String(error)
          });
        });
      }
      
      // Load available tracks for Starter tier check
      logInfo('Loading available tracks for Starter tier check');
      getTracks().then(tracks => {
        setAvailableTracks(tracks);
        logInfo('Available tracks loaded for Starter tier check', { trackCount: tracks.length });
      }).catch(error => {
        logError('Failed to load available tracks for Starter tier check', { 
          error: error instanceof Error ? error.message : String(error)
        });
      });
    }
  }, [user, driveLinked]);

  // Load share count and determine user role
  useEffect(() => {
    if (id && user && driveLinked) {
      logInfo('Loading track detail', { trackId: id });
      getTrackById(id).then(data => {
        setTrack(data || null);
        setLoading(false);
        // Update autocomplete lists for artist and release
        if (data) {
          logInfo('Track detail loaded successfully', { 
            trackId: id, 
            trackName: data.name,
            hasPromoImages: !!data.promoImages,
            promoImageCount: data.promoImages?.length || 0
          });
          if (data.promoImages) setPromoImages(data.promoImages);
          
          // Initialize audio files from track data
          if (data.audioFiles) {
            const audioFilesData = data.audioFiles.map(file => ({
              id: file.fileId,
              name: file.name,
              fileId: file.fileId,
              isPreview: file.isPreview
            }));
            setAudioFiles(audioFilesData);
          }
          
          let artists = JSON.parse(localStorage.getItem('cassette_artists') || '[]');
          let releases = JSON.parse(localStorage.getItem('cassette_releases') || '[]');
          if (data.artist && !artists.includes(data.artist)) {
            artists.push(data.artist);
            localStorage.setItem('cassette_artists', JSON.stringify(artists));
          }
          if (data.release && !releases.includes(data.release)) {
            releases.push(data.release);
            localStorage.setItem('cassette_releases', JSON.stringify(releases));
          }
        } else {
          logInfo('Track not found', { trackId: id });
        }
      }).catch(error => {
        logError('Failed to load track detail', { 
          trackId: id,
          error: error instanceof Error ? error.message : String(error) 
        });
        setLoading(false);
      });
    }
  }, [id, user, driveLinked]);

  useEffect(() => {
    if (Track) {
      setPromoPitch({ en: Track.pitchEn || '', native: Track.pitchNative || '' });
      setNotes(Track.notes || '');
    }
  }, [Track]);

  // Unified logic for determining ownership, sharing permissions, and Starter tier limits
  useEffect(() => {
    if (id && user && Track) {
      listShares(id).then(shares => {
        setShareCount(shares.length);
        
        let isTrackOwner = false;
        let userRole = 'viewer';
        
        // Check ownership - prioritize track.userId check first (more reliable)
        // This matches the logic in TrackCollaborators.tsx
        if (Track.userId === user.uid) {
          // User is the track creator/owner
          isTrackOwner = true;
          userRole = 'owner';
        } else if (!Track.userId) {
          // Track doesn't have userId field (legacy track)
          // Fallback: check if user appears as owner in shares
          const userIsOwnerFromShares = shares.some((share: TrackShare) => share.ownerUserId === user.uid);
          if (userIsOwnerFromShares) {
            isTrackOwner = true;
            userRole = 'owner';
          } else if (shares.length === 0) {
            // Legacy track with no shares - if user can access it, they're likely the owner
            logInfo('Legacy track without userId field - assuming user is owner', { trackId: id, userId: user.uid });
            isTrackOwner = true;
            userRole = 'owner';
          } else {
            // Legacy track with shares - user is a collaborator
            const userShare = shares.find((share: TrackShare) => share.collaboratorUserId === user.uid);
            if (userShare) {
              userRole = userShare.role;
              isTrackOwner = false;
            } else {
              userRole = 'viewer';
            isTrackOwner = false;
            }
          }
        } else if (shares.length === 0) {
          // Track has userId but it's not the current user, and no shares exist
          // User has no access
          isTrackOwner = false;
          userRole = 'viewer';
        } else {
          // Track has a different owner - check if user is a collaborator
          const userShare = shares.find((share: TrackShare) => share.collaboratorUserId === user.uid);
          if (userShare) {
            userRole = userShare.role;
            isTrackOwner = false;
          } else {
            // User has no access
            userRole = 'viewer';
            isTrackOwner = false;
          }
        }
        
        // Get base permissions from role
        let basePermissions = getTrackPermissions(userRole, isTrackOwner);
        
        // Apply Starter tier limits for owners (can only edit first 3 tracks)
        if (isTrackOwner && userType === 'starter' && availableTracks.length > 0) {
          const sorted = [...availableTracks].sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
          const editableIds = sorted.slice(0, 3).map(t => t.id);
          const canEditThisTrack = editableIds.includes(Track.id);
          
          // If owner can't edit this track due to tier limits, restrict permissions
          if (!canEditThisTrack) {
            basePermissions = {
              ...basePermissions,
              canUploadAudio: false,
              canUploadPromo: false,
              canAddAudioComments: false,
              canEditMetadata: false,
              canDelete: false,
              canShare: false,
            };
          }
        }
        
        setIsOwner(isTrackOwner);
        setPermissions(basePermissions);
        
      }).catch(error => {
        logError('Failed to load share count', { trackId: id, error });
        // Fallback: assume no access if we can't load shares
        setIsOwner(false);
        setPermissions({
          canRead: false,
          canUploadAudio: false,
          canUploadPromo: false,
          canAddAudioComments: false,
          canEditMetadata: false,
          canDelete: false,
          canShare: false,
        });
      });
    }
  }, [id, user, Track, userType, availableTracks]);

  const openImportModal = async () => {
    try {
      // Filter available tracks for import modal (only tracks with promo images)
      const otherTracks = availableTracks.filter(t => t.id !== Track?.id && t.promoImages && t.promoImages.length > 0);
      setAvailableTracks(otherTracks);
      setShowImportModal(true);
    } catch (error) {
      toast.error(t('errors.failedToLoadTracksForImport'));
    }
  };

  const importFromSelectedTrack = async (sourceTrackId: string) => {
    if (!Track) return;
    
    setShowImportModal(false);

    // Find the selected track to get the file count
    const selectedTrack = availableTracks.find(t => t.id === sourceTrackId);
    const fileCount = selectedTrack?.promoImages?.length || 0;
    
    // Set the file count for the progress bar
    setCurrentImportFileCount(fileCount);

    try {
      // Ensure WebSocket connection is established before starting import
      if (user?.uid) {
        await asyncUploadService.connect(user.uid);
      }

      // Call the backend import API using the service
      await importPromoImages(Track.id, sourceTrackId);

      // Import is now handled via WebSocket notifications - no need for loading toast
      // The progress bar in PromoMaterialsSection will show the real-time progress

    } catch (error) {
      toast.error(t('errors.importFailed'));
      setCurrentImportFileCount(0);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <CassetteLoader size={80} variant="overlay" text={t('track.loadingTrack')} />
      </div>
    );
  }
  if (!Track) {
    return <div className="text-center py-10 text-red-500">{t('notFound')}</div>;
  }

  const binaryBlocked = Track.binaryAccess === false;
  const binaryReason = Track.binaryInactiveReason;
  const requiredProvider = Track.requiredStorageProvider;
  const binaryViewerRole = isOwner ? 'owner' as const : 'collaborator' as const;

  return (
    <div className="min-h-screen flex flex-col w-full">
      <div className="flex-1 w-full">
        {showUploadGateBanner && (
          <div className="mb-4">
            <StorageRequiredBanner />
          </div>
        )}
        {binaryBlocked && (
          <div className="mb-4 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10">
            <p className="text-sm text-[var(--color-text)]">
              {t(binaryInactiveBodyKey(binaryReason), {
                provider: providerLabelForCloudError(requiredProvider, t),
                defaultValue: 'Project files are not available yet. Check cloud storage in Settings.',
              })}
              {binaryReason === 'acl_failed' && isOwner && Track.id && (
                <>
                  {' '}
                  <Link
                    to={`/tracks/${Track.id}/collaborators`}
                    className="underline font-medium"
                    style={{ color: 'var(--brand-from)' }}
                  >
                    {t('sharing.sync.button', 'Sync sharing')}
                  </Link>
                </>
              )}
              {((!isOwner &&
                (binaryReason === 'provider_mismatch' ||
                  binaryReason === 'collaborator_unlinked' ||
                  binaryReason === 'collaborator_auth_invalid')) ||
                (isOwner &&
                  (binaryReason === 'owner_auth_invalid' || binaryReason === 'owner_trial_cloud'))) && (
                <>
                  {' '}
                  <Link to="/settings" className="underline font-medium" style={{ color: 'var(--brand-from)' }}>
                    {binaryReason === 'owner_auth_invalid' || binaryReason === 'collaborator_auth_invalid'
                      ? t('sharing.binaryUnavailable.cta.reconnectProvider', {
                          provider: providerLabelForCloudError(requiredProvider, t),
                          defaultValue: 'Reconnect cloud',
                        })
                      : t('sharing.binaryUnavailable.cta.connectProvider', {
                          provider: providerLabelForCloudError(requiredProvider, t),
                          defaultValue: 'Link storage',
                        })}
                  </Link>
                </>
              )}
              {!isOwner &&
                (binaryReason === 'owner_auth_invalid' || binaryReason === 'owner_trial_cloud') && (
                  <span className="block mt-1 text-xs text-[var(--color-muted)]">
                    {t('cloudErrors.ownerMustReconnect', {
                      provider: providerLabelForCloudError(requiredProvider, t),
                      defaultValue: 'Ask the track owner to reconnect their cloud account.',
                    })}
                  </span>
                )}
            </p>
          </div>
        )}
        {!gatedPermissions.canEditMetadata && userType === 'starter' && isOwner && canMutate && (
          <div className="mb-4 p-4 border rounded-xl flex items-center gap-3" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
            <LockClosedIcon className="w-6 h-6 text-yellow-500" />
            <div>
              <div className="font-semibold text-yellow-700">Artist subscription required</div>
              <div className="text-sm text-yellow-700">Only your first 3 tracks are editable on the Starter plan. <a href='/upgrade' className='underline' style={{ color: 'var(--brand-from)' }}>Upgrade to Artist</a> to edit unlimited tracks.</div>
            </div>
          </div>
        )}
        <div>
          
          {/* Top header row only (name, status, share). Align with accordions */}
          <div className="w-full mb-6">
            <div className="bg-[var(--color-surface)] rounded-xl shadow-sm border border-[var(--color-border)] p-6 w-full">
                {/* Track Header Row - Name, Status, Edit Button */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Track Name (always read-only here) */}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-3xl font-bold text-[var(--color-text)] break-words">{Track.name}</h1>
                  </div>
                  
                  {/* Status, Share, and Edit Button */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-shrink-0">
                    <span 
                      className="inline-flex px-4 py-2 text-sm font-semibold rounded-full border"
                      style={{
                        backgroundColor: getStatusColor(Track.status).split(' ')[0],
                        color: getStatusColor(Track.status).split(' ')[1],
                        borderColor: getStatusColor(Track.status).split(' ')[2]
                      }}
                    >
                      {t(`track.statusLabels.${Track.status}`)}
                    </span>
                    {!editMode && (
                      <>
                        <Link
                          to={`/tracks/${id}/collaborators`}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 border"
                          style={{
                            backgroundColor: 'var(--collab-blue-bg)',
                            color: 'var(--collab-blue-text)',
                            borderColor: 'var(--collab-blue-border)'
                          }}
                          onMouseEnter={(e) => {
                            const target = e.target as HTMLElement;
                            target.style.backgroundColor = 'var(--collab-blue-text)';
                            target.style.color = 'var(--collab-blue-bg)';
                          }}
                          onMouseLeave={(e) => {
                            const target = e.target as HTMLElement;
                            target.style.backgroundColor = 'var(--collab-blue-bg)';
                            target.style.color = 'var(--collab-blue-text)';
                          }}
                          title={isOwner ? t('share.manageCollaborators') : t('share.viewCollaborators')}
                          data-testid="share-button"
                        >
                          <UsersIcon className="w-4 h-4" />
                          {shareCount > 0 ? t('share.sharedWith', { count: shareCount }) : t('buttons.share')}
                        </Link>
                      </>
                    )}
                  </div>
                </div>
            </div>
          </div>
          {/* Content divided into Production and Promotion areas */}
          <div className="space-y-8">
            {/* Production Area */}
            <div>
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2">{t('track.production')}</h2>
                  <p className="text-[var(--color-muted)]">{t('track.productionDescription')}</p>
                </div>
                <button
                  onClick={toggleAllAccordions}
                  className={classNames(
                    'px-4 py-2 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:ring-offset-2',
                    isSketchy
                      ? 'sketch-btn rounded-sm'
                      : 'rounded-lg transform hover:scale-[1.02] border'
                  )}
                  style={
                    isSketchy
                      ? undefined
                      : {
                          backgroundColor: 'var(--color-surface)',
                          color: 'var(--color-text)',
                          borderColor: 'var(--color-border)',
                        }
                  }
                >
                  {allAccordionsOpen ? t('track.collapseAll') : t('track.expandAll')}
                </button>
              </div>
              <div className="space-y-4 w-full">
                {/* Artwork accordion */}
                <SketchSection
                  key={`artwork-${allAccordionsOpen}`}
                  title={t('track.artwork') || 'Artwork'} 
                  defaultOpen={allAccordionsOpen}
                  icon={<ImageIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                  indicator={
                    Track.artwork || artwork ? (
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-gray-400" />
                    )
                  }
                >
                  <TrackHeader
                    artwork={artwork}
                    setArtwork={setArtwork}
                    showArtworkUpload={showArtworkUpload}
                    setShowArtworkUpload={setShowArtworkUpload}
                    Track={Track}
                    setTrack={setTrack}
                    permissions={gatedPermissions}
                    size="large"
                  />
                </SketchSection>

                {/* Metadata accordion */}
                <SketchSection
                  key={`metadata-${allAccordionsOpen}`} 
                  title={t('track.metadata')} 
                  defaultOpen={allAccordionsOpen}
                  icon={<InfoIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                >
                  {/* Metadata display or edit */}
                  {!editMode ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-6">
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.name')}:</span>
                          <p className="text-lg text-[var(--color-text)] font-medium break-words" data-testid="track-name-value">{Track.name}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.isSingle')}:</span>
                          <p className="text-lg text-[var(--color-text)] font-medium">{Track.isSingle ? t('common.yes') : t('common.no')}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.artist')}:</span>
                          <p className="text-lg text-[var(--color-text)] font-medium" data-testid="track-artist-value">{Track.artist || '-'}</p>
                        </div>
                        {!Track.isSingle && (
                          <div>
                            <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.release')}:</span>
                            <p className="text-lg text-[var(--color-text)] font-medium">{Track.release || '-'}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.releaseDateLabel')}:</span>
                          <p className="text-lg text-[var(--color-text)] font-medium" data-testid="track-release-date-value">{Track.releaseDate || '-'}</p>
                        </div>
                      </div>
                      <div className="space-y-6">
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.status')}:</span>
                          <p className="text-lg text-[var(--color-text)] font-medium" data-testid="track-status-value">{t(`track.statusLabels.${Track.status}`)}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.lastUpdatedLabel')}:</span>
                          <p className="text-lg text-[var(--color-text)] font-medium">{Track.lastUpdated ? new Date(Track.lastUpdated).toLocaleDateString() : '-'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.multilink')}:</span>
                          <p className="text-lg text-[var(--color-text)] font-medium break-all" data-testid="track-multilink-value">
                            {Track.multilink ? (
                              <a href={Track.multilink} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--brand-from)' }} onMouseEnter={(e) => (e.target as HTMLElement).style.color = 'var(--brand-to)'} onMouseLeave={(e) => (e.target as HTMLElement).style.color = 'var(--brand-from)'}>
                                {Track.multilink}
                              </a>
                            ) : '-'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.progress')}:</span>
                          <div className="flex items-center gap-3 mt-2">
                            <SketchProgressBar
                              value={Track.progress || 0}
                              variant={isSketchy ? 'scribble' : 'bar'}
                              className="flex-1"
                              data-testid="track-progress-bar"
                            />
                            <span className="text-lg font-semibold text-[var(--color-text)] w-12 text-right" data-testid="track-progress-value">{Track.progress || 0}%</span>
                          </div>
                        </div>
                      </div>
                      {/* Inline edit button below header */}
                      {gatedPermissions.canEditMetadata && !editMode && (
                        <div className="pt-1">
                          <button
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] btn-brand text-white"
                            onClick={startEditMode}
                            data-testid="track-edit-button"
                          >
                            <Pencil className="w-4 h-4" />
                            {t('buttons.edit') || 'Edit'}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.name')}:</span>
                            <input name="name" value={editData.name} onChange={handleChange} className="mt-1 w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent" placeholder={t('track.enterTrackName')} />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.artist')}:</span>
                            <input name="artist" value={editData.artist} onChange={handleChange} className="mt-1 w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent" placeholder={t('track.artist')} list="artist-list" ref={artistInputRef} />
                            <datalist id="artist-list">{JSON.parse(localStorage.getItem('cassette_artists') || '[]').map((artist: string) => (<option key={artist} value={artist} />))}</datalist>
                          </div>
                          {!editData.isSingle && (
                            <div>
                              <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.release')}:</span>
                              <input name="release" value={editData.release} onChange={handleChange} className="mt-1 w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent" placeholder={t('track.release')} list="release-list" ref={releaseInputRef} />
                              <datalist id="release-list">{JSON.parse(localStorage.getItem('cassette_releases') || '[]').map((release: string) => (<option key={release} value={release} />))}</datalist>
                            </div>
                          )}
                          <div>
                            <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.releaseDateLabel')}:</span>
                            <input name="releaseDate" type="date" value={editData.releaseDate} onChange={handleChange} className="mt-1 w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent" />
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.status')}:</span>
                            <select name="status" value={editData.status} onChange={handleChange} className="mt-1 w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent">
                              {TRACK_STATUS_VALUES.map(status => (<option key={status} value={status}>{t(`track.statusLabels.${status}`)}</option>))}
                            </select>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.multilink')}:</span>
                            <input name="multilink" value={editData.multilink} onChange={handleChange} className="mt-1 w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent" placeholder="https://ffm.to/yourtrack" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.progress')}:</span>
                            <div className="flex items-center gap-3 mt-2">
                              <input type="range" name="progress" min={0} max={100} step={5} value={editData.progress ?? 0} onChange={handleChange} className="flex-1" style={{ accentColor: 'var(--brand-from)' }} />
                              <span className="text-sm font-semibold text-[var(--color-text)] w-12 text-right">{editData.progress ?? 0}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 pt-4 border-t border-[var(--color-border)]">
                        <span className="text-sm font-medium text-[var(--color-muted)]">{t('track.isSingle')}:</span>
                        <div className="mt-2 flex items-center">
                          <button type="button" onClick={() => { const newValue = !editData.isSingle; setEditData(prev => ({ ...prev, isSingle: newValue })); }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] ${editData.isSingle ? '' : 'bg-gray-200'}`} style={{ backgroundColor: editData.isSingle ? 'var(--brand-from)' : undefined }}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editData.isSingle ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                          <span className="ml-3 text-sm text-[var(--color-text)]/80">{editData.isSingle ? t('common.yes') : t('common.no')}</span>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6 pt-4 border-t border-[var(--color-border)]">
                        <button className="px-4 py-2 text-white rounded-lg hover:opacity-90 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: 'var(--brand-from)' }} onClick={handleSave} type="button" disabled={isSaving || !gatedPermissions.canEditMetadata} data-testid="track-save-button">{isSaving ? (<div className="flex items-center gap-2"><CassetteLoader size={48} variant="inline" /><span>Saving...</span></div>) : (t('buttons.save') || 'Save')}</button>
                        <button className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }} onClick={handleCancel} type="button" disabled={isSaving} data-testid="track-cancel-button">{t('buttons.cancel') || 'Cancel'}</button>
                      </div>
                    </>
                  )}
                </SketchSection>

                <SketchSection
                  key={`audio-${allAccordionsOpen}`}
                  title={t('track.audioAssets')} 
                  defaultOpen={allAccordionsOpen && audioFiles.length > 0}
                  icon={<MusicIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                  count={audioFiles.length}
                >
                  <AudioFilesSection 
                    trackId={Track?.id || ''} 
                    audioFiles={audioFiles} 
                    onAudioFilesChange={setAudioFiles} 
                    audioFolderUrl={Track?.audioFolderUrl}
                    permissions={gatedPermissions}
                    binaryAccess={Track.binaryAccess}
                    binaryInactiveReason={binaryReason}
                    requiredStorageProvider={requiredProvider}
                    viewerRole={binaryViewerRole}
                    storageProvider={Track.storageProvider}
                  />
                </SketchSection>
                
                <SketchSection
                  key={`notes-${allAccordionsOpen}`}
                  title={t('notes.notes')} 
                  defaultOpen={allAccordionsOpen && !!notes}
                  icon={<MessageSquareIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                >
                  <NotesSection trackId={Track.id} initialNotes={notes} permissions={gatedPermissions} />
                </SketchSection>
              </div>
            </div>


            {/* Promotion Area */}
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2">{t('track.promotion')}</h2>
                <p className="text-[var(--color-muted)]">{t('track.promotionDescription')}</p>
              </div>
              <div className="space-y-4 w-full">
                <SketchSection
                  key={`promo-pitches-${allAccordionsOpen}`}
                  title={t('track.promoPitches')} 
                  defaultOpen={allAccordionsOpen && !!(promoPitch.en || promoPitch.native)}
                  icon={<FileTextIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                >
                  <PromoPitchesSection
                    promoPitch={promoPitch}
                    setPromoPitch={setPromoPitch}
                    secondLang={secondLang}
                    setSecondLang={setSecondLang}
                    trackId={Track.id}
                    permissions={gatedPermissions}
                  />
                </SketchSection>
                
                <SketchSection
                  key={`promo-images-${allAccordionsOpen}`}
                  title={t('track.promoImages')} 
                  defaultOpen={allAccordionsOpen && promoImages.length > 0}
                  icon={<CameraIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                  count={promoImages.length}
                >
                  <PromoImagesSection
                    promoImages={promoImages}
                    setPromoImages={setPromoImages}
                    importFromLastTrack={openImportModal}
                    cloudFolderUrl={Track.promoFolderUrl}
                    trackId={Track.id}
                    importFileCount={currentImportFileCount}
                    permissions={gatedPermissions}
                    isOwner={isOwner}
                    binaryAccess={Track.binaryAccess}
                    binaryInactiveReason={binaryReason}
                    requiredStorageProvider={requiredProvider}
                    viewerRole={binaryViewerRole}
                    storageProvider={Track.storageProvider}
                  />
                </SketchSection>
                
                <SketchSection
                  key={`videos-${allAccordionsOpen}`}
                  title={t('track.verticalVideos.videosLabel')} 
                  defaultOpen={allAccordionsOpen && Array.isArray(Track.videoAssets) && Track.videoAssets.length > 0}
                  icon={<VideoIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                  count={Array.isArray(Track.videoAssets) ? Track.videoAssets.length : 0}
                >
                  <VerticalVideosSection track={Track} permissions={gatedPermissions} section="videos" binaryAccess={Track.binaryAccess} binaryInactiveReason={binaryReason} requiredStorageProvider={requiredProvider} viewerRole={binaryViewerRole} />
                </SketchSection>
                
                <SketchSection
                  key={`footages-${allAccordionsOpen}`}
                  title={t('track.verticalVideos.footagesLabel')} 
                  defaultOpen={allAccordionsOpen && Array.isArray(Track.videoFootages) && Track.videoFootages.length > 0}
                  icon={<FilmIcon className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />}
                  count={Array.isArray(Track.videoFootages) ? Track.videoFootages.length : 0}
                >
                  <VerticalVideosSection track={Track} permissions={gatedPermissions} section="footages" binaryAccess={Track.binaryAccess} binaryInactiveReason={binaryReason} requiredStorageProvider={requiredProvider} viewerRole={binaryViewerRole} />
                </SketchSection>
              </div>
            </div>
          </div>
        </div>

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'var(--modal-backdrop)' }}>
            <div className="rounded-xl shadow-xl max-w-md w-full mx-4 p-6 border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>{t('promo.importFromTrack')}</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>{t('promo.selectTrackToImport')}</p>
              
              <div className="max-h-60 overflow-y-auto space-y-2">
                {availableTracks.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">{t('promo.noTracksWithImages')}</p>
                ) : (
                  availableTracks.map((track) => (
                    <button
                      key={track.id}
                      onClick={() => importFromSelectedTrack(track.id)}
                      className="w-full text-left p-3 rounded-lg transition-colors border"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <div className="font-medium" style={{ color: 'var(--color-text)' }}>{track.name}</div>
                      <div className="text-sm" style={{ color: 'var(--color-muted)' }}>
                        {track.promoImages?.length || 0} {t('promo.images')}
                      </div>
                    </button>
                  ))
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 font-medium"
                  style={{ color: 'var(--color-text)' }}
                >
                  {t('buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default TrackDetail; 